/**
 * Coda ticket V3 (prototipo L100-L160).
 *
 * Parte (a): modello, fetch, tab, ricerca, filtri, viste salvate. La logica e
 * TRASPORTATA INVARIATA da `TicketQueue.tsx`: stessi filtri, stesso debounce,
 * stesso trattamento di 401/403, stesse viste salvate. Quel file e stato
 * rimosso col Task 13 (i riferimenti `TicketQueue.tsx:NNN` nei commenti sono
 * citazioni di provenienza, leggibili nella storia git).
 * Parte (b): riga di coda, stati di lista e paginazione. Montato dal Centro
 * Ticket admin (Task 10) e dall'area rappresentante (Task 13).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownWideNarrow,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Filter,
  LifeBuoy,
  Loader2,
  Plus,
  Search,
  Trash2,
  ArrowRightLeft,
  X,
} from 'lucide-react';
import { useToast } from '../../../contexts/ToastContext';
import ConfirmDialog from '../../ConfirmDialog';
import { S3EmptyState, S3Menu } from './primitives';
import QueueRow from './QueueRow';
import QueueTableRow, { QueueTableHeader } from './QueueTableRow';
import { PriorityLegend } from './PriorityIcon';
import { useNarrow } from './useNarrow';
import { useSupportLook } from '../v4/lookContext';
import AnchoredLayer from '../v4/AnchoredLayer';
import { slaBadge } from './visual';
import { classifySla } from '../../../utils/supportSla';
import { buildKpis, kpiIsActive, type QueueQuickFilter } from './kpiModel';
import {
  activeFilterCount,
  buildListParams,
  columnSortLabel,
  nextColumnSort,
  pillChoosesStatus,
  searchesAllStates,
  TAB_CLOSED,
  buildQueueRow,
  channelFilterLabel,
  channelFilterVisible,
  countLabel,
  filtersFromView,
  isValidViewName,
  queueTabsFor,
  hasCopyQueue,
  TAB_COPY,
  sortLabel,
  sortRowsClientSide,
  viewFiltersFromState,
  type ColumnSort,
  type ColumnSortKey,
  type QueueFilterState,
  type QueueSort,
} from './queueModel';
import {
  ORIGIN_LABELS,
  PRIORITY_LABELS,
  REQUESTER_TYPES,
  REQUESTER_TYPE_LABELS,
  STATUS_ACTIVE,
  STATUS_ACTIVE_LABEL,
  STATUS_LABELS,
  TICKET_ORIGINS,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
} from '../constants';
import type { GroupLite, OperatorSupportApi, SavedView, StatsData, TicketListItem } from '../../../services/supportApi';

const MONO_META: React.CSSProperties = {
  font: "600 9px 'JetBrains Mono', monospace",
  color: 'var(--a2-fnt)',
};

export interface SupportQueueProps {
  api: OperatorSupportApi;
  /** Apre il dettaglio del ticket (la pagina usa `api.ticketPath`). */
  onOpenTicket: (id: string) => void;
  /** 401/403 sulla lista: la pagina decide dove rimandare l'utente. */
  /** 401/403 dalla lista: lo status permette al chiamante di distinguere sessione scaduta (401) da permesso mancante (403). */
  onAuthError?: (status?: number) => void;
  /**
   * Espone il refetch della lista (usato dal realtime della pagina). Puo
   * restituire una funzione di pulizia, invocata allo smontaggio della coda:
   * serve alla pagina per non tenersi un riferimento a un albero morto.
   */
  onReady?: (refetch: () => void) => void | (() => void);
  /** Colonna stretta (layout a due pannelli): la card riempie l'altezza disponibile. */
  compact?: boolean;
  /**
   * `card` (default): riga a tre livelli, per la colonna stretta dello split
   * view. `table`: riga a colonne con intestazione, per la coda a tutta
   * larghezza (area rappresentante); sotto i 1024px torna alle card.
   */
  layout?: 'card' | 'table';
  /** Intestazione interna: la si nasconde quando la pagina ne ha gia una. */
  showHeader?: boolean;
  /**
   * Contenuto a destra del titolo dell'intestazione interna (es. il link
   * "Guida all'uso" dell'area rappresentante). Ignorato con `showHeader={false}`.
   */
  headerActions?: React.ReactNode;
  /** Contatori interni: idem (nel Centro Ticket V3 stanno nella barra titolo). */
  showStats?: boolean;
  /**
   * Canali nella select: i propri (default), tutti quelli visibili, oppure
   * (`direct`, Task 31) i canali in lavorazione diretta di cui si e componente.
   */
  groupsScope?: 'mine' | 'all' | 'direct';
  /** Vista «I miei canali» (Task 31): solo i ticket dei canali in lavorazione diretta. */
  directOnly?: boolean;
  /** Vista «Smistamento» (Task 31): chip «Da smistare» e «Non presi in carico». */
  quickFilters?: boolean;
  /**
   * Azione rapida «Trasferisci» in riga (Task 31): se presente, ogni riga porta
   * l'icona che apre la modale di trasferimento senza aprire il ticket.
   */
  onRowTransfer?: (ticket: TicketListItem) => void;
  /**
   * Se il ticket della riga e trasferibile dall'attore (default si): come nella
   * scheda, l'assegnatario esterno non trasferisce. Il backend resta l'arbitro.
   */
  canRowTransfer?: (ticket: TicketListItem) => boolean;
  /** Messaggio della lista vuota (default «Nessun ticket trovato.»). */
  emptyMessage?: React.ReactNode;
  /** Filtri estesi: canale d'ingresso, tag, tipo richiedente (Centro Ticket admin). */
  advancedFilters?: boolean;
  /** Canale preselezionato (es. `?canale=feedback`). */
  initialGroupId?: string;
  /** Ticket aperto nel pannello affiancato: la riga viene evidenziata. */
  selectedTicketId?: string;
  /**
   * Filtro imposto dall'esterno (pillole KPI della barra titolo). Viene
   * applicato ad ogni cambio di `nonce`, cosi lo stesso filtro puo essere
   * riapplicato: i campi assenti restano invariati.
   */
  externalFilter?: { status?: string; unread?: boolean; quick?: QueueQuickFilter; nonce: number };
  /** Notifica alla pagina il filtro corrente (per evidenziare la pillola attiva). */
  onFilterChange?: (f: { status: string; unread: boolean; quick: QueueQuickFilter }) => void;
  /** Toggle di ordinamento della pagina corrente (L121-L123). Default true. */
  sortable?: boolean;
  /** In narrow: la pagina passa alla schermata di dettaglio (prototipo L588). */
  onRequestDetail?: () => void;
  /**
   * Id dell'utente corrente (T34, facoltativo): in veste v4 il suo avatar in
   * riga porta l'anello verde (N14). Senza, nessun anello; la v3 lo ignora.
   */
  selfId?: string;
}

const SupportQueue: React.FC<SupportQueueProps> = ({
  api,
  onOpenTicket,
  onAuthError,
  onReady,
  compact = false,
  layout = 'card',
  showHeader = true,
  headerActions,
  showStats = true,
  groupsScope = 'mine',
  directOnly = false,
  quickFilters = false,
  onRowTransfer,
  canRowTransfer,
  emptyMessage,
  advancedFilters = false,
  initialGroupId,
  selectedTicketId,
  externalFilter,
  onFilterChange,
  sortable = true,
  onRequestDetail,
  selfId,
}) => {
  const toast = useToast();
  const narrow = useNarrow();
  const tableLayout = layout === 'table' && !narrow;
  const v4 = useSupportLook() === 'v4';
  const filtersTriggerRef = useRef<HTMLButtonElement>(null);

  // `api` non e garantito referenzialmente stabile (un adapter costruito inline
  // nel render cambierebbe identita ad ogni giro): tenerlo in una ref evita che
  // gli effetti di caricamento entrino in loop. Gli effetti leggono apiRef.current
  // e NON hanno `api` fra le dipendenze (TicketQueue.tsx:100-105).
  const apiRef = useRef(api);
  apiRef.current = api;

  const [tab, setTab] = useState<string>('all');
  // Vista iniziale: solo i ticket su cui c'e ancora lavoro (niente risolti/chiusi).
  // Una vista salvata applicata dall'utente sovrascrive questo default.
  const [status, setStatus] = useState<string>(STATUS_ACTIVE);
  // Lo stato e stato SCELTO (Filtri, vista salvata, pillola KPI) o e ancora il
  // default? Col default, una ricerca guarda in tutti gli stati (searchesAllStates).
  const [statusChosen, setStatusChosen] = useState<boolean>(false);
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  // «Nascondi uniti e indesiderati»: filtro del backend (hideNoise), spento di default.
  const [hideNoise, setHideNoise] = useState<boolean>(false);
  const [priority, setPriority] = useState<string>('');
  const [groupId, setGroupId] = useState<string>(initialGroupId || '');
  const [search, setSearch] = useState<string>('');

  // Filtri estesi: attivi solo con `advancedFilters` (restano vuoti altrove).
  const [origin, setOrigin] = useState<string>('');
  const [tag, setTag] = useState<string>('');
  const [requesterType, setRequesterType] = useState<string>('');
  // Filtro rapido della vista «Smistamento» (Task 31): '' = nessuno.
  const [quick, setQuick] = useState<QueueQuickFilter>('');

  const [showFilters, setShowFilters] = useState<boolean>(false);
  // Gruppo "Altri filtri" collassabile: i tre filtri estesi non affollano il
  // pannello di chi non li usa (ruling 9).
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [sort, setSort] = useState<QueueSort>('priority');
  // Ordinamento di colonna (solo tabella, lato server): null = ordine di sempre.
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  // In narrow la tabella torna alle card: niente intestazioni, ordine di sempre.
  const activeColumnSort = tableLayout ? columnSort : null;
  const onColumnSort = useCallback((key: ColumnSortKey) => {
    setColumnSort((prev) => nextColumnSort(prev, key));
    setPage(1);
  }, []);

  // "Azzera filtri": torna al default della coda (stato Attivi, nessun altro
  // filtro, ricerca vuota). Tab e ordinamento restano: non sono filtri.
  const resetFilters = () => {
    setStatus(STATUS_ACTIVE);
    setStatusChosen(false);
    setPriority('');
    setGroupId(initialGroupId || '');
    setOrigin('');
    setTag('');
    setRequesterType('');
    setUnreadOnly(false);
    setHideNoise(false);
    setQuick('');
    setSearch('');
    setPage(1);
  };

  const [items, setItems] = useState<TicketListItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const listRef = useRef<HTMLDivElement>(null);

  const [groups, setGroups] = useState<GroupLite[]>([]);
  // Task 46: il chip «Bozze da approvare» compare a chi puo approvare almeno su un canale.
  const [canApproveAny, setCanApproveAny] = useState<boolean>(false);
  // Scheda «In copia» (parte A): solo se un canale dell'operatore ha copie.
  const [hasCopies, setHasCopies] = useState<boolean>(false);
  // `/groups` gia letto: prima di allora `hasCopies` false non vuol dire «nessuna copia».
  const copiesKnownRef = useRef(false);
  const [stats, setStats] = useState<StatsData | null>(null);
  // Canali predefiniti fra quelli caricati: in v4 prendono il tono neutro (N10).
  const defaultGroupIds = useMemo(
    () => new Set(groups.filter((g) => g.inboundDefault).map((g) => g._id)),
    [groups]
  );

  // ===== Viste salvate =====
  const [views, setViews] = useState<SavedView[]>([]);
  const [viewsOpen, setViewsOpen] = useState<boolean>(false);
  const [viewName, setViewName] = useState<string>('');
  const [viewToDelete, setViewToDelete] = useState<SavedView | null>(null);
  const viewsTriggerRef = useRef<HTMLButtonElement>(null);
  /** v4: pannello del popover «Filtri» (focus dentro all'apertura). */
  const filtersPanelRef = useRef<HTMLDivElement>(null);

  // v4: il popover «Filtri» e un dialog non modale; all'apertura il focus entra
  // nel primo controllo attivo (in v3 i filtri sono in linea: nessun effetto).
  useEffect(() => {
    if (!v4 || !showFilters) return;
    filtersPanelRef.current
      ?.querySelector<HTMLElement>('select:not([disabled]), input:not([disabled]), button:not([disabled])')
      ?.focus({ preventScroll: true });
  }, [v4, showFilters]);

  /** v4: chiusura del popover; se il focus era dentro (Esc) torna su «Filtri». */
  const closeFiltersV4 = useCallback(() => {
    const inside = !!filtersPanelRef.current?.contains(document.activeElement);
    setShowFilters(false);
    if (inside) filtersTriggerRef.current?.focus({ preventScroll: true });
  }, []);

  const filterState: QueueFilterState = { status, priority, groupId, origin, tag, requesterType, unreadOnly, hideNoise };
  const fltCount = activeFilterCount(filterState);

  useEffect(() => {
    apiRef.current
      .getViews()
      .then(setViews)
      .catch(() => {
        /* il menu viste resta vuoto */
      });
  }, []);

  const applyView = (v: SavedView) => {
    const f = filtersFromView(v);
    // «In copia» senza copie (canali gia noti): la scheda non c'e, si apre «Tutti».
    setTab(f.tab === TAB_COPY && copiesKnownRef.current && !hasCopies ? 'all' : f.tab);
    setStatus(f.status);
    setStatusChosen(true);
    setUnreadOnly(f.unreadOnly);
    setPriority(f.priority);
    setGroupId(f.groupId);
    setSearch(f.search);
    // Una vista salvata non conosce i filtri rapidi: si riparte senza.
    setQuick('');
    setPage(1);
    setViewsOpen(false);
  };

  const saveView = async () => {
    if (!isValidViewName(viewName)) return;
    try {
      await apiRef.current.createView(viewName.trim(), viewFiltersFromState(filterState, { tab, search }));
      setViewName('');
      const v = await apiRef.current.getViews();
      setViews(v);
      toast.success('Vista salvata');
    } catch {
      toast.error('Errore salvataggio vista');
    }
  };

  const confirmDeleteView = async () => {
    if (!viewToDelete) return;
    const targetId = viewToDelete._id;
    try {
      await apiRef.current.deleteView(targetId);
      setViews((prev) => prev.filter((x) => x._id !== targetId));
      toast.success('Vista eliminata');
    } catch {
      toast.error('Errore eliminazione vista');
    }
  };

  // Filtro imposto dalla pagina (pillole KPI): si riapplica ad ogni `nonce`,
  // anche se i valori non sono cambiati.
  const externalNonce = externalFilter?.nonce;
  useEffect(() => {
    if (externalNonce === undefined) return;
    if (externalFilter?.status !== undefined) {
      setStatus(externalFilter.status);
      // La pillola ATTIVI (default, senza non letti ne filtro rapido) non sceglie lo stato.
      setStatusChosen(
        pillChoosesStatus({
          status: externalFilter.status,
          unread: externalFilter.unread ?? unreadOnly,
          quick: externalFilter.quick ?? quick,
        })
      );
    }
    if (externalFilter?.unread !== undefined) setUnreadOnly(externalFilter.unread);
    if (externalFilter?.quick !== undefined) setQuick(externalFilter.quick);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalNonce]);

  // La pagina segue il filtro corrente per evidenziare la pillola attiva.
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;
  useEffect(() => {
    onFilterChangeRef.current?.({ status, unread: unreadOnly, quick });
  }, [status, unreadOnly, quick]);

  // Il canale preselezionato puo arrivare dopo (slug risolto dalla pagina).
  useEffect(() => {
    if (initialGroupId !== undefined) {
      setGroupId(initialGroupId);
      setPage(1);
    }
  }, [initialGroupId]);

  // ===== Canali visibili all'operatore =====
  useEffect(() => {
    apiRef.current
      .getGroups()
      .then((res) => {
        setGroups((groupsScope === 'all' ? res.all : groupsScope === 'direct' ? res.direct : res.mine) || []);
        setCanApproveAny(
          [...(res.mine || []), ...(res.all || []), ...(res.direct || [])].some((g) => g.canApprove === true)
        );
        const copies = hasCopyQueue(res);
        copiesKnownRef.current = true;
        setHasCopies(copies);
        // Vista salvata con «In copia» quando le copie non ci sono più: si torna a «Tutti».
        if (!copies) setTab((t) => (t === TAB_COPY ? 'all' : t));
      })
      .catch(() => {
        /* la select canale resta vuota */
      });
  }, [groupsScope]);

  const sortKey = activeColumnSort?.key;
  const sortDir = activeColumnSort?.dir;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRef.current.listTickets(
        buildListParams(
          { status, priority, groupId, origin, tag, requesterType, unreadOnly, hideNoise },
          {
            tab,
            search,
            page,
            quick,
            directOnly,
            statusChosen,
            columnSort: sortKey && sortDir ? { key: sortKey, dir: sortDir } : null,
          }
        )
      );
      setItems(res.items || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        onAuthError?.(err.response?.status);
        return;
      }
      toast.error('Errore caricamento ticket');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status, priority, groupId, search, origin, tag, requesterType, unreadOnly, hideNoise, page, quick, directOnly, statusChosen, sortKey, sortDir]);

  // Debounce semplice sulla ricerca; carica subito al cambio degli altri filtri/pagina
  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Statistiche coda: al mount e ad ogni refetch realtime, cosi il contatore
  // "Da leggere" segue gli eventi socket come la lista.
  const loadStats = useCallback(() => {
    if (!showStats) return;
    apiRef.current
      .getStats()
      .then(setStats)
      .catch(() => {
        /* i contatori restano nascosti */
      });
  }, [showStats]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Refetch esposto alla pagina (realtime): sempre l'ultima `load`.
  const loadRef = useRef(load);
  loadRef.current = load;
  const loadStatsRef = useRef(loadStats);
  loadStatsRef.current = loadStats;
  useEffect(() => {
    const cleanup = onReady?.(() => {
      loadRef.current();
      loadStatsRef.current();
    });
    return typeof cleanup === 'function' ? cleanup : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Apertura di una riga: in narrow la pagina passa alla schermata di
   * dettaglio (ruling 2).
   */
  const openRow = useCallback(
    (id: string) => {
      onOpenTicket(id);
      onRequestDetail?.();
    },
    [onOpenTicket, onRequestDetail]
  );

  // Con un ordinamento di colonna la pagina resta nell'ordine del server; senza,
  // il toggle riordina la pagina (con una ricerca i non conclusi restano in cima).
  const rows = activeColumnSort || !sortable ? items : sortRowsClientSide(items, sort, { activeFirst: search.trim() !== '' });
  // Ricerca col solo stato di default: si cerca in tutti gli stati (lo si dice nel contatore).
  const allStatesSearch = searchesAllStates(status, { tab, search, statusChosen });
  // Bottone d'ordinamento: con una colonna attiva ne mostra il nome e la toglie.
  const sortButtonLabel = activeColumnSort ? columnSortLabel(activeColumnSort) : sortLabel(sort);
  const sortButtonTitle = activeColumnSort
    ? 'Torna all’ordine predefinito'
    : sort === 'priority'
      ? 'Ordina per non letti'
      : 'Ordina per priorità';
  const onSortButton = () => {
    if (activeColumnSort) {
      setColumnSort(null);
      setPage(1);
      return;
    }
    setSort((s) => (s === 'priority' ? 'unread' : 'priority'));
  };
  const allStatesNote = allStatesSearch ? (
    <span style={{ ...MONO_META, letterSpacing: '.06em' }}>RICERCA IN TUTTI GLI STATI</span>
  ) : null;
  // Un solo istante per tutti i chip SLA della pagina.
  const now = new Date();

  // Cambio pagina: si riparte dall'alto, altrimenti la nuova pagina si apre a
  // meta lista sulla posizione di scorrimento della precedente.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page]);

  // Controlli condivisi dalle due vesti (stesso DOM della v3).
  const statusSelect = (
    <select
      className={`s3-sel${status !== STATUS_ACTIVE ? ' s3-sel--on' : ''}`}
      value={status}
      disabled={tab === TAB_CLOSED}
      title={tab === TAB_CLOSED ? 'Sul tab "Risolti e chiusi" lo stato e fissato dal tab' : undefined}
      onChange={(e) => {
        setStatus(e.target.value);
        setStatusChosen(true);
        setPage(1);
      }}
      aria-label="Filtro stato"
    >
      <option value={STATUS_ACTIVE}>{STATUS_ACTIVE_LABEL}</option>
      <option value="">Tutti gli stati</option>
      {TICKET_STATUSES.map((s) => (
        <option key={s} value={s}>
          {STATUS_LABELS[s]}
        </option>
      ))}
    </select>
  );

  const prioritySelect = (
    <select
      className={`s3-sel${priority ? ' s3-sel--on' : ''}`}
      value={priority}
      onChange={(e) => {
        setPriority(e.target.value);
        setPage(1);
      }}
      aria-label="Filtro priorità"
    >
      <option value="">Tutte le priorità</option>
      {TICKET_PRIORITIES.map((p) => (
        <option key={p} value={p}>
          {PRIORITY_LABELS[p]}
        </option>
      ))}
    </select>
  );

  const groupSelect = (
    <select
      className={`s3-sel${groupId ? ' s3-sel--on' : ''}`}
      style={{ gridColumn: '1 / -1' }}
      value={groupId}
      onChange={(e) => {
        setGroupId(e.target.value);
        setPage(1);
      }}
      aria-label="Filtro canale"
    >
      <option value="">Tutti i canali</option>
      {groups.filter((g) => channelFilterVisible(g, groupId)).map((g) => (
        <option key={g._id} value={g._id}>
          {channelFilterLabel(g)}
        </option>
      ))}
    </select>
  );

  const originSelect = (
    <select
      className={`s3-sel${origin ? ' s3-sel--on' : ''}`}
      value={origin}
      onChange={(e) => {
        setOrigin(e.target.value);
        setPage(1);
      }}
      aria-label="Filtro origine"
    >
      <option value="">Tutte le origini</option>
      {TICKET_ORIGINS.map((o) => (
        <option key={o} value={o}>
          {ORIGIN_LABELS[o]}
        </option>
      ))}
    </select>
  );

  const requesterTypeSelect = (
    <select
      className={`s3-sel${requesterType ? ' s3-sel--on' : ''}`}
      value={requesterType}
      onChange={(e) => {
        setRequesterType(e.target.value);
        setPage(1);
      }}
      aria-label="Filtro tipo richiedente"
    >
      <option value="">Tutti i richiedenti</option>
      {REQUESTER_TYPES.map((r) => (
        <option key={r} value={r}>
          {REQUESTER_TYPE_LABELS[r]}
        </option>
      ))}
    </select>
  );

  const hideNoiseToggle = (
    <label className="s3-check" style={{ gridColumn: '1 / -1' }} title="Esclude i ticket uniti in un altro e quelli col mittente ignorato">
      <input
        type="checkbox"
        checked={hideNoise}
        onChange={(e) => {
          setHideNoise(e.target.checked);
          setPage(1);
        }}
      />
      Nascondi uniti e indesiderati
    </label>
  );

  const tagInput = (
    <input
      className={`s3-sel${tag ? ' s3-sel--on' : ''}`}
      style={{ gridColumn: '1 / -1', paddingLeft: 8 }}
      value={tag}
      onChange={(e) => {
        setTag(e.target.value);
        setPage(1);
      }}
      placeholder="Tag"
      aria-label="Filtro tag"
    />
  );

  const searchInput = (
    <input
      className="s3-search"
      value={search}
      onChange={(e) => {
        setSearch(e.target.value);
        setPage(1);
      }}
      placeholder="Cerca numero, oggetto, associato…"
      aria-label="Cerca ticket"
    />
  );

  const viewsPanel = (
    <>
      {views.length === 0 && (
        <div className="s3-empty" style={{ margin: 6 }}>
          Nessuna vista salvata.
        </div>
      )}
      {views.map((v) => (
        <div key={v._id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button type="button" className="s3-menuitem" onClick={() => applyView(v)}>
            {v.nome}
          </button>
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-pill--icon s3-pill--danger"
            aria-label={`Elimina vista ${v.nome}`}
            onClick={() => {
              // Il pannello si chiude: la conferma non deve restare
              // sotto un dropdown ancora aperto.
              setViewsOpen(false);
              setViewToDelete(v);
            }}
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 4, padding: 5, borderTop: '1px solid var(--a2-bd2)' }}>
        <input
          className="s3-search"
          style={{ paddingLeft: 12 }}
          value={viewName}
          onChange={(e) => setViewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveView();
          }}
          placeholder="Nome vista"
          aria-label="Nome della nuova vista"
        />
        <button
          type="button"
          className="s3-pill s3-pill--sm s3-pill--icon s3-pill--primary"
          onClick={saveView}
          disabled={!isValidViewName(viewName)}
          title="Salva i filtri correnti come vista"
          aria-label="Salva vista"
        >
          <Plus size={12} />
        </button>
      </div>
      <p className="s3-meta" style={{ padding: '0 6px 6px' }}>
        Salva i filtri correnti come vista
      </p>
    </>
  );

  const unreadChip = (
    <span className="s3-badge s3-badge--green" style={{ alignSelf: 'flex-start', gap: 4 }}>
      Solo non letti
      <button
        type="button"
        className="s3-link"
        style={{ display: 'inline-flex', color: 'inherit' }}
        aria-label="Rimuovi il filtro sui non letti"
        onClick={() => {
          setUnreadOnly(false);
          setPage(1);
        }}
      >
        <X size={12} />
      </button>
    </span>
  );

  // ===== Veste v4 della barra (T34, `Centro Ticket v4.dc.html` L186-L247) =====
  // Stessi controlli e stessi handler della v3: cambiano solo la disposizione
  // (bottoni con etichetta, popover filtri 360 px con etichette mono) e la
  // riga del contatore con la legenda pallino/rombo (N13).
  const v4Toolbar = (
    <>
      <div className="s4-qbar">
        <div className="s4-qbar__search">
          <Search size={14} className="s4-qbar__lens" aria-hidden="true" />
          {searchInput}
        </div>

        <div className="s4-pop s4-pop--filters">
          <button
            ref={filtersTriggerRef}
            type="button"
            className={`s3-pill${showFilters || fltCount > 0 ? ' s3-pill--active' : ''}`}
            title="Filtri"
            aria-haspopup="dialog"
            aria-expanded={showFilters}
            onClick={() => setShowFilters((v) => !v)}
          >
            <Filter size={13} aria-hidden="true" />
            Filtri
            {fltCount > 0 && <span className="s4-fcount">{fltCount}</span>}
          </button>
          <AnchoredLayer open={showFilters} anchorRef={filtersTriggerRef} className="s4-pop s4-pop--filters">
          <S3Menu
            open={showFilters}
            onClose={closeFiltersV4}
            asMenu={false}
            ariaLabel="Filtri"
            triggerRef={filtersTriggerRef}
          >
            <div className="s4-fpop" ref={filtersPanelRef}>
              <div className="s4-field">
                <span className="s4-flabel" aria-hidden="true">Stato</span>
                {statusSelect}
              </div>
              {tab === TAB_CLOSED && <p className="s4-fnote">Lo stato è fissato dal tab «Risolti e chiusi».</p>}
              <div className="s4-field">
                <span className="s4-flabel" aria-hidden="true">Priorità</span>
                {prioritySelect}
              </div>
              <div className="s4-field">
                <span className="s4-flabel" aria-hidden="true">Canale</span>
                {groupSelect}
              </div>
              {hideNoiseToggle}
              {advancedFilters && (
                <>
                  <button
                    type="button"
                    className="s4-fmore"
                    aria-expanded={showAdvanced}
                    onClick={() => setShowAdvanced((v) => !v)}
                  >
                    <span>Altri filtri</span>
                    <span aria-hidden="true">{showAdvanced ? '▲' : '▼'}</span>
                  </button>
                  {showAdvanced && (
                    <>
                      <div className="s4-field">
                        <span className="s4-flabel" aria-hidden="true">Origine</span>
                        {originSelect}
                      </div>
                      <div className="s4-field">
                        <span className="s4-flabel" aria-hidden="true">Tipo richiedente</span>
                        {requesterTypeSelect}
                      </div>
                      <div className="s4-field">
                        <span className="s4-flabel" aria-hidden="true">Tag</span>
                        {tagInput}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </S3Menu>
          </AnchoredLayer>
        </div>

        <div className="s4-pop">
          <button
            ref={viewsTriggerRef}
            type="button"
            className={`s3-pill${viewsOpen ? ' s3-pill--active' : ''}`}
            title="Viste salvate"
            aria-haspopup="dialog"
            aria-expanded={viewsOpen}
            onClick={() => setViewsOpen((v) => !v)}
          >
            <Bookmark size={13} aria-hidden="true" />
            Viste
          </button>
          <AnchoredLayer open={viewsOpen} anchorRef={viewsTriggerRef} className="s4-pop">
          <S3Menu
            open={viewsOpen}
            onClose={() => setViewsOpen(false)}
            asMenu={false}
            ariaLabel="Viste salvate"
            triggerRef={viewsTriggerRef}
          >
            <div className="s4-flabel s4-vhead" aria-hidden="true">
              Viste salvate
            </div>
            {viewsPanel}
          </S3Menu>
          </AnchoredLayer>
        </div>

        {sortable && (
          <button
            type="button"
            className="s3-pill s4-sortbtn"
            title={sortButtonTitle}
            onClick={onSortButton}
          >
            {sortButtonLabel}
          </button>
        )}

        {/* Azzera filtri: compare solo quando c'e qualcosa da azzerare */}
        {(fltCount > 0 || search.trim()) && (
          <button type="button" className="s3-link s4-reset" onClick={resetFilters}>
            <X size={12} aria-hidden="true" /> Azzera filtri{search.trim() ? ' e ricerca' : ''}
          </button>
        )}
      </div>

      {/* Contatore, chip «Solo non letti» e legenda (L238-L247) */}
      <div className="s4-qcount">
        <div className="s4-qcount__left">
          <span style={{ ...MONO_META, letterSpacing: '.08em' }}>{countLabel(total)}</span>
          {allStatesNote}
          {unreadOnly && unreadChip}
        </div>
        <div className="s4-legend">
          <span>
            {/* In tabella il messaggio non letto e la riga verde: campione della riga */}
            <span className={tableLayout ? 's3-legend-row' : 's4-legend__dot'} aria-hidden="true" />
            messaggio non letto
          </span>
          <span>
            <span className={`s4-legend__dot s4-legend__dot--note${tableLayout ? ' s3-legend-note' : ''}`} aria-hidden="true" />
            nota da leggere
          </span>
          {/* Simboli della priorita: solo dove la tabella li usa */}
          {tableLayout && (
            <>
              <span className="s3-legend-sep" aria-hidden="true" />
              <PriorityLegend />
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <section
      className={v4 ? 'sup3 s3-card s4-queue' : 'sup3 s3-card'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
        height: compact ? '100%' : undefined,
      }}
    >
      {/* ── Testata (L103-L137) ── */}
      <div style={{ padding: '12px 14px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {showHeader && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: 'var(--a2-green-500)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}
            >
              <LifeBuoy size={17} />
            </span>
            <h2 className="s3-h2">Ticket di supporto</h2>
            {headerActions && <span style={{ marginLeft: 'auto' }}>{headerActions}</span>}
          </div>
        )}

        {/* Contatori interni (area rappresentante): nel Centro Ticket V3 la
            pagina li spegne con `showStats={false}` e usa la barra titolo.
            "Attivi" e "Da leggere" restano filtri cliccabili come le card di
            oggi (TicketQueue.tsx:325-377); gli altri sono informativi. */}
        {showStats && stats && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {buildKpis(stats)
              .filter((k) => k.key !== 'total')
              .map((k) =>
                k.clickable && k.filter ? (
                  <button
                    key={k.key}
                    type="button"
                    className={`s3-badge s3-badge--${kpiIsActive(k, { status, unread: unreadOnly, quick }) ? 'green' : 'chip'}`}
                    style={{ border: 'none', cursor: 'pointer' }}
                    title={k.label}
                    aria-pressed={kpiIsActive(k, { status, unread: unreadOnly, quick })}
                    onClick={() => {
                      setStatus(k.filter!.status);
                      setStatusChosen(pillChoosesStatus(k.filter!));
                      setUnreadOnly(k.filter!.unread);
                      // «BOZZE» (Task 46) porta il filtro rapido; le altre lo tolgono.
                      setQuick(k.filter!.quick || '');
                      setPage(1);
                    }}
                  >
                    {k.value} · {k.label}
                  </button>
                ) : (
                  <span key={k.key} className="s3-badge s3-badge--chip" title={k.label}>
                    {k.value} · {k.label}
                  </span>
                )
              )}
          </div>
        )}

        {/* Tab (L104-L111). Nessuna pastiglia di conteggio: il backend non
            espone i conteggi per tab e non se ne aggiungono (ruling 16). */}
        <div className="s3-seg">
          {queueTabsFor(api.kind, { hasCopies }).map((t) => (
            <button
              key={t.key}
              type="button"
              className={`s3-segbtn${tab === t.key ? ' s3-segbtn--on' : ''}`}
              aria-pressed={tab === t.key}
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filtri rapidi della vista «Smistamento» (Task 31): uno alla volta,
            un secondo clic sullo stesso chip lo spegne. */}
        {(quickFilters || canApproveAny) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Filtri rapidi">
            {([
              ...(quickFilters
                ? [
                    { key: 'dispatch', label: 'Da smistare', hint: 'Ticket attivi nel canale predefinito (contatti dal sito ed email inoltrate)' },
                    { key: 'unassignedOpen', label: 'Non presi in carico', hint: 'Ticket aperti senza assegnatari, in qualsiasi canale' },
                  ]
                : []),
              // Task 46: solo a chi puo approvare le bozze di almeno un canale.
              ...(canApproveAny
                ? [{ key: 'pending', label: 'Bozze da approvare', hint: 'Ticket con risposte in bozza in attesa della tua approvazione' }]
                : []),
            ] as { key: QueueQuickFilter; label: string; hint: string }[]).map((c) => (
              <button
                key={c.key}
                type="button"
                className={`s3-badge s3-badge--${quick === c.key ? 'green' : 'chip'}`}
                style={{ border: 'none', cursor: 'pointer' }}
                title={c.hint}
                aria-pressed={quick === c.key}
                onClick={() => {
                  setQuick((q) => (q === c.key ? '' : c.key));
                  setPage(1);
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {v4 ? (
          v4Toolbar
        ) : (
          <>
            {/* Ricerca + Filtri + Viste + Ordinamento (L112-L124) */}
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                <Search
                  size={13}
                  style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--a2-fnt)' }}
                />
                {searchInput}
              </div>

              <button
                type="button"
                className={`s3-pill s3-pill--icon${showFilters || fltCount > 0 ? ' s3-pill--active' : ''}`}
                style={fltCount > 0 ? { width: 'auto', padding: '0 11px' } : undefined}
                title="Filtri"
                aria-expanded={showFilters}
                onClick={() => setShowFilters((v) => !v)}
              >
                <Filter size={12} />
                {fltCount > 0 && (
                  <span
                    style={{
                      font: "700 9.5px 'JetBrains Mono', monospace",
                      color: '#fff',
                      background: 'var(--a2-green-500)',
                      borderRadius: 999,
                      padding: '1px 6px',
                    }}
                  >
                    {fltCount}
                  </span>
                )}
              </button>

              <div style={{ position: 'relative' }}>
                <button
                  ref={viewsTriggerRef}
                  type="button"
                  className={`s3-pill s3-pill--icon${viewsOpen ? ' s3-pill--active' : ''}`}
                  title="Viste salvate"
                  aria-haspopup="dialog"
                  aria-expanded={viewsOpen}
                  onClick={() => setViewsOpen((v) => !v)}
                >
                  <Bookmark size={12} />
                </button>
                {/* `asMenu={false}`: il pannello contiene anche il campo "Nome
                    vista", quindi non e un `role="menu"` di sole voci; con il nome
                    accessibile diventa un `role="dialog"`. `triggerRef` tiene il
                    bottone fuori dal click-outside, altrimenti il toggle
                    richiuderebbe e riaprirebbe il pannello nello stesso click. */}
                <S3Menu
                  open={viewsOpen}
                  onClose={() => setViewsOpen(false)}
                  asMenu={false}
                  ariaLabel="Viste salvate"
                  triggerRef={viewsTriggerRef}
                >
                  {viewsPanel}
                </S3Menu>
              </div>

              {sortable && (
                <button
                  type="button"
                  className="s3-pill s3-pill--icon"
                  title={sortButtonTitle}
                  onClick={onSortButton}
                >
                  <ArrowDownWideNarrow size={13} />
                </button>
              )}
            </div>

            {/* Pannello filtri (L125-L133) */}
            {showFilters && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, animation: 's3FadeIn .15s ease-out' }}>
                {statusSelect}

                {prioritySelect}

                {groupSelect}

                {hideNoiseToggle}

                {advancedFilters && (
                  <>
                    <button
                      type="button"
                      className="s3-link"
                      style={{ gridColumn: '1 / -1', textAlign: 'left' }}
                      aria-expanded={showAdvanced}
                      onClick={() => setShowAdvanced((v) => !v)}
                    >
                      {showAdvanced ? '▲' : '▼'} Altri filtri
                    </button>
                    {showAdvanced && (
                      <>
                        {originSelect}

                        {requesterTypeSelect}

                        {tagInput}
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Azzera filtri: compare solo quando c'e qualcosa da azzerare */}
            {(fltCount > 0 || search.trim()) && (
              <button
                type="button"
                className="s3-link"
                style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4, font: "600 11px 'DM Sans', sans-serif" }}
                onClick={resetFilters}
              >
                <X size={12} /> Azzera filtri{search.trim() ? ' e ricerca' : ''}
              </button>
            )}

            {/* Chip removibile "Solo non letti" (TicketQueue.tsx:636-653) */}
            {unreadOnly && unreadChip}

            {/* Riga contatore + ordinamento (L134-L137) */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                paddingBottom: 8,
                borderBottom: '1px solid var(--a2-bd2)',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...MONO_META, letterSpacing: '.08em' }}>{countLabel(total)}</span>
                {allStatesNote}
              </span>
              {(sortable || activeColumnSort) && <span style={{ ...MONO_META, letterSpacing: '.06em' }}>{sortButtonLabel}</span>}
            </div>
            {/* Tabella v3: legenda dei simboli della priorita (la v4 la ha nella sua riga della legenda) */}
            {tableLayout && (
              <div className="s3-legend-v3">
                <span className="s3-legend-item">
                  <span className="s3-legend-row" aria-hidden="true" />
                  messaggio non letto
                </span>
                <span className="s3-legend-item">
                  <span className="s3-dot-note s3-legend-note" aria-hidden="true" />
                  nota da leggere
                </span>
                <span className="s3-legend-sep" aria-hidden="true" />
                <PriorityLegend />
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Lista scorrevole (L139-L165) ── */}
      <div
        ref={listRef}
        style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', padding: 6, minHeight: 0 }}
        role="region"
        aria-label="Elenco ticket"
        aria-busy={loading}
      >
        {/* Intestazione della tabella: resta montata anche durante il caricamento
            (le righe di prima restano in memoria), cosi il focus del bottone
            d'ordinamento appena premuto non si perde. */}
        {tableLayout && rows.length > 0 && <QueueTableHeader sort={activeColumnSort} onSort={onColumnSort} />}
        {loading && (
          <div className="s3-empty" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Loader2 size={14} className="animate-spin" /> Caricamento…
          </div>
        )}
        {!loading && rows.length === 0 && <S3EmptyState>{emptyMessage ?? 'Nessun ticket trovato.'}</S3EmptyState>}
        {/* Lo SLA si classifica al render, come faceva `SlaChip`: il residuo
            dipende dall'ora corrente, non da cio che ha risposto il backend.
            Un solo `now` per l'intera pagina, cosi le righe sono confrontabili. */}
        {!loading &&
          rows.map((t) => {
            const Row = tableLayout ? QueueTableRow : QueueRow;
            const rowEl = (
              <Row
                key={t._id}
                row={buildQueueRow(t, { selectedTicketId, selfId, defaultGroupIds })}
                sla={slaBadge(classifySla(t, now))}
                onOpen={openRow}
              />
            );
            if (!onRowTransfer) return rowEl;
            const closed = t.status === 'chiuso';
            const notMember = canRowTransfer ? !canRowTransfer(t) : false;
            // Azione rapida accanto alla riga (la riga e un <button>: niente
            // bottoni annidati). Posto riservato anche al futuro suggerimento AI.
            return (
              <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0 }}>{rowEl}</div>
                <button
                  type="button"
                  className="s3-pill s3-pill--sm s3-pill--icon"
                  // Stessa regola del menu della scheda: un ticket chiuso non si trasferisce.
                  // Assegnatario esterno: solo i componenti del canale lo spostano.
                  disabled={closed || notMember}
                  title={
                    closed
                      ? 'Ticket chiuso: non trasferibile'
                      : notMember
                        ? 'Solo i componenti del canale possono trasferire il ticket'
                        : 'Trasferisci a un altro canale'
                  }
                  aria-label={`Trasferisci il ticket #${t.ticketNumber}`}
                  onClick={() => onRowTransfer(t)}
                >
                  <ArrowRightLeft size={12} />
                </button>
              </div>
            );
          })}
      </div>

      {/* ── Paginazione (parita TicketQueue.tsx:744-771) ── */}
      {!loading && totalPages > 1 && (
        <div
          className={v4 ? 's4-pager' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderTop: '1px solid var(--a2-bd2)',
          }}
        >
          <span className="s3-meta" style={{ flex: 1 }}>
            {total} ticket · pagina {page} di {totalPages}
          </span>
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-pill--icon"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Pagina precedente"
          >
            <ChevronLeft size={13} />
          </button>
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-pill--icon"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Pagina successiva"
          >
            <ChevronRight size={13} />
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(viewToDelete)}
        type="danger"
        title="Elimina vista"
        message={`Vuoi eliminare la vista "${viewToDelete?.nome || ''}"?`}
        confirmText="Elimina"
        onConfirm={confirmDeleteView}
        onClose={() => setViewToDelete(null)}
      />
    </section>
  );
};

export default SupportQueue;
